import {
  AgentPolicyControllerService as Policy,
  type AgentNativePolicyLogicModel,
  type AgentPolicyScopeModel,
} from "@/api";
import type {
  AgentPolicyCoverage,
  AgentPolicyRecommendation,
  AgentScope,
  ApplyPoliciesRequest,
  ApplyPoliciesResult,
  DraftPolicy,
  EffectiveEnforcement,
  EffectiveEnforcementQuery,
  GenerateDraftPolicyRequest,
  NativePolicy,
  PolicyApplicationPreview,
  PolicyImplementation,
  PolicyImplementationsQuery,
  PolicyReview,
  PolicyReviewQuery,
  ReapplyPolicyDefinitionRequest,
  RetryPolicyDeploymentRequest,
  ScopedPolicyCatalogItem,
  ScopedPolicyCatalogQuery,
  UpdateNativePolicyRequest,
} from "@/api/temp/AgentEstateService";
import type { RequestOptions } from "@/hooks/usePaginateQuery";

type ScopeQuery = { environmentId: string; scope: AgentScope };

/** The generated models are all-optional with plain-string unions; the UI types are the contract. */
const asResponse = <T>(response: Promise<unknown>) => response as Promise<T>;

/** The demo's scope, unmapped: the backend matches the UI's request. */
const asScope = (scope: AgentScope) => scope as AgentPolicyScopeModel;

const scoped = ({ environmentId, scope }: ScopeQuery) => ({
  environmentId,
  scope: asScope(scope),
});

/**
 * The generated agent policy APIs behind the UI's own request and response
 * types. Both modes send the UI's request as-is (the backend matches it); a
 * live call only adds the generated client's calling convention - `{ body }`
 * and ids in the path. The mock registry keys on `method.name`.
 * Hence shorthand methods, not function declarations: a method's `name` is its
 * property key, which the production minifier keeps, while it renames plain
 * functions.
 */
export const AgentPolicyApi = {
  resolveAgentPolicyRecommendationsUsingPost(
    query: ScopeQuery,
    options?: RequestOptions,
  ) {
    return asResponse<AgentPolicyRecommendation[]>(
      Policy.resolveAgentPolicyRecommendationsUsingPost(
        { body: scoped(query) },
        options,
      ),
    );
  },
  resolveAgentPolicyCoverageUsingPost(
    query: ScopeQuery,
    options?: RequestOptions,
  ) {
    return asResponse<AgentPolicyCoverage>(
      Policy.resolveAgentPolicyCoverageUsingPost(
        { body: scoped(query) },
        options,
      ),
    );
  },
  resolvePolicyCatalogUsingPost(
    { environmentId, scope, ...search }: ScopedPolicyCatalogQuery,
    options?: RequestOptions,
  ) {
    return asResponse<ScopedPolicyCatalogItem[]>(
      Policy.resolvePolicyCatalogUsingPost(
        { body: { ...search, ...scoped({ environmentId, scope }) } },
        options,
      ),
    );
  },
  getPolicyReviewUsingPost(
    { policyIds, agentId, ...query }: PolicyReviewQuery,
    options?: RequestOptions,
  ) {
    return asResponse<PolicyReview>(
      Policy.getPolicyReviewUsingPost(
        { body: { ...scoped(query), policyIds, agentId } },
        options,
      ),
    );
  },
  generateDraftPolicyUsingPost({
    environmentId,
    intent,
    scope,
  }: GenerateDraftPolicyRequest) {
    return asResponse<DraftPolicy>(
      Policy.generateDraftPolicyUsingPost({
        body: {
          environmentId,
          intent,
          scope: scope && asScope(scope),
        },
      }),
    );
  },
  previewAgentPoliciesUsingPost({ policyIds, ...query }: ApplyPoliciesRequest) {
    return asResponse<PolicyApplicationPreview>(
      Policy.previewAgentPoliciesUsingPost({
        body: { ...scoped(query), policyIds },
      }),
    );
  },
  applyPoliciesUsingPost({
    policyIds,
    previewId,
    ...query
  }: ApplyPoliciesRequest) {
    return asResponse<ApplyPoliciesResult>(
      Policy.applyPoliciesUsingPost({
        // As the UI holds it; the panel always previews before applying.
        body: { ...scoped(query), policyIds, previewId: previewId as string },
      }),
    );
  },
  getEffectiveEnforcementUsingGet(
    query: EffectiveEnforcementQuery,
    options?: RequestOptions,
  ) {
    return asResponse<EffectiveEnforcement | undefined>(
      Policy.getEffectiveEnforcementUsingGet(query, options),
    );
  },
  listPolicyImplementationsUsingGet(
    query: PolicyImplementationsQuery,
    options?: RequestOptions,
  ) {
    return asResponse<PolicyImplementation[]>(
      Policy.listPolicyImplementationsUsingGet(query, options),
    );
  },
  updateNativePolicyUsingPut({
    environmentId,
    nativePolicyId,
    current,
  }: UpdateNativePolicyRequest) {
    return asResponse<NativePolicy>(
      Policy.updateNativePolicyUsingPut({
        nativePolicyId,
        body: {
          environmentId,
          nativePolicyId,
          // Same effect values; the generated type is an enum.
          current: current as unknown as AgentNativePolicyLogicModel,
        },
      }),
    );
  },
  reapplyPolicyDefinitionUsingPost({
    environmentId,
    policyId,
    acknowledgeOverrides,
  }: ReapplyPolicyDefinitionRequest) {
    return asResponse<PolicyImplementation>(
      Policy.reapplyPolicyDefinitionUsingPost({
        policyId,
        body: {
          environmentId,
          policyId,
          acknowledgeOverrides,
        },
      }),
    );
  },
  retryPolicyDeploymentUsingPost({
    environmentId,
    policyId,
  }: RetryPolicyDeploymentRequest) {
    return asResponse<PolicyImplementation>(
      Policy.retryPolicyDeploymentUsingPost({
        policyId,
        body: { environmentId, policyId },
      }),
    );
  },
};
